import { resolveTourUseIpp } from './tourUseIpp'

export { resolveTourUseIpp } from './tourUseIpp'

/**
 * Pannellum pitch (deg) for a floor-plan neighbor at horizontal distance
 * `distance` (plan pixels) from the camera.
 *
 * Canonical (live didge) formula — keep for parity:
 *   H_px = height_inches / scale   (DB default scale === 1 → H = height)
 *   pitch = atan2(hypot(d, H_px), H_px) * 180/π − 90
 * Pitch 0 = horizon, negative = look down. Near neighbors sit shallower than
 * pure −atan(H/d). Historical note: git main briefly used constant −15° for
 * uncalibrated floors; that is not live and not used here.
 *
 * Legacy calibrated scale (≠ 1) always feeds H (same as live). It is not
 * subject to the neighbor-spacing gate.
 *
 * API inches/px: auto-trust only `scale_source === 'floor_plan_scale'`.
 * NexLoc / Progress are ignored unless `?tourUseIpp=1` (requires API
 * `?include_untrusted_tour_metrology=1` so the payload actually includes them;
 * `fetchFloor` adds that when tourUseIpp is set; useFloorImages refetches
 * when the flag changes via the router search string).
 * Neighbor-spacing helpers (`isNeighborSpacingPlausibleFt`, d_ref) remain for
 * opt-in max-range and a future pending-source allowlist; they are not applied
 * on the default trust path today.
 *
 * Presentation ceiling: off by default. Optional A/B: `?tourPitchCeiling=-15`.
 * Max range: off by default. Opt-in: `?tourMaxRangeFt=35`.
 */

/** Optional A/B presentation clamp (negative deg). Not the default placer. */
export const DEFAULT_TOUR_PITCH_CEILING_DEG = -15

/** How many nearest imaged points contribute to stable d_ref (max-range). */
export const STABLE_REF_NEIGHBOR_COUNT = 8

/**
 * Max-range default: disabled (match prod hotspot set).
 * Opt-in via `?tourMaxRangeFt=35`.
 */
export const DEFAULT_TOUR_MAX_RANGE_FT = Number.POSITIVE_INFINITY

/** Scale sources the client may auto-trust for tour pitch (Phase 1). */
export const AUTO_TRUSTED_SCALE_SOURCES = ['floor_plan_scale'] as const

/**
 * Sources that may be allowlisted later after validation (Phase 2+).
 * Not auto-trusted today; require ?tourUseIpp=1 (+ API untrusted metrology).
 */
export const SCALE_SOURCES_PENDING_VALIDATION = [
  'nexloc_resolution',
  'progress_ceiling_sf',
] as const

/**
 * Plausible median neighbor spacing (feet). Kept for tests and a future
 * pending-source allowlist; not enforced on the default trust path.
 */
export const MIN_TRUSTED_NEIGHBOR_SPACING_FT = 4
export const MAX_TRUSTED_NEIGHBOR_SPACING_FT = 25

export function isFloorPlanScaleCalibrated(
  scale: number | null | undefined,
): boolean {
  return Number.isFinite(scale) && (scale as number) > 0 && scale !== 1
}

export function isScaleSourceAutoTrusted(
  scaleSource: string | null | undefined,
): boolean {
  return (
    scaleSource != null &&
    (AUTO_TRUSTED_SCALE_SOURCES as readonly string[]).includes(scaleSource)
  )
}

export function isNeighborSpacingPlausibleFt(
  spacingFt: number | null | undefined,
): boolean {
  if (spacingFt == null || !Number.isFinite(spacingFt)) {
    return false
  }
  return (
    spacingFt >= MIN_TRUSTED_NEIGHBOR_SPACING_FT &&
    spacingFt <= MAX_TRUSTED_NEIGHBOR_SPACING_FT
  )
}

function isPendingScaleSource(
  scaleSource: string | null | undefined,
): boolean {
  return (
    scaleSource != null &&
    (SCALE_SOURCES_PENDING_VALIDATION as readonly string[]).includes(
      scaleSource,
    )
  )
}

/**
 * Resolve inches/px for tour pitch / max-range.
 * - Legacy calibrated scale: always returned (live prod).
 * - floor_plan_scale API ipp: always returned when present.
 * - NexLoc / Progress: only with `?tourUseIpp=1` (no spacing gate on this path).
 * `referenceDistancePx` is accepted for callers/max-range; unused for trust.
 */
export function resolveTrustedTourInchesPerPixel(options: {
  apiInchesPerPixel?: number | null
  scaleSource?: string | null
  legacyScale?: number | null
  search?: string
  /** Unused for trust today; kept for max-range / future allowlist callers. */
  referenceDistancePx?: number
}): number | null {
  const force = resolveTourUseIpp(options.search ?? '')
  const apiIpp =
    Number.isFinite(options.apiInchesPerPixel) &&
    (options.apiInchesPerPixel as number) > 0
      ? (options.apiInchesPerPixel as number)
      : null
  const legacyIpp = isFloorPlanScaleCalibrated(options.legacyScale)
    ? (options.legacyScale as number)
    : null

  let candidate: number | null = null

  if (force && apiIpp != null) {
    // A/B: use API ipp as-is (gate skipped so the flag is never a no-op).
    candidate = apiIpp
  } else if (isPendingScaleSource(options.scaleSource)) {
    // Never auto-trust NexLoc / Progress.
    candidate = null
  } else if (isScaleSourceAutoTrusted(options.scaleSource) && apiIpp != null) {
    candidate = apiIpp
  } else if (legacyIpp != null) {
    // Live prod: calibrated legacy scale always applies.
    candidate = legacyIpp
  }

  if (candidate == null || !(candidate > 0)) {
    return null
  }

  return candidate
}

/**
 * @deprecated Prefer resolveTrustedTourInchesPerPixel. Kept for narrow unit tests.
 * Does not apply trust gates — treats positive api ipp as usable.
 */
export function resolveTourInchesPerPixel(
  inchesPerPixel: number | null | undefined,
  legacyScale: number | null | undefined,
): number | null {
  if (Number.isFinite(inchesPerPixel) && (inchesPerPixel as number) > 0) {
    return inchesPerPixel as number
  }
  if (isFloorPlanScaleCalibrated(legacyScale)) {
    return legacyScale as number
  }
  return null
}

export function medianPositive(values: number[]): number {
  const sorted = values
    .filter(v => Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b)
  if (sorted.length === 0) {
    return 0
  }
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * Stable reference distance: median of the N closest positive distances.
 */
export function stableReferenceDistancePx(
  distances: number[],
  maxCount: number = STABLE_REF_NEIGHBOR_COUNT,
): number {
  const sorted = distances
    .filter(v => Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b)
  if (sorted.length === 0) {
    return 0
  }
  return medianPositive(sorted.slice(0, Math.max(1, maxCount)))
}

/**
 * Optional A/B ceiling from query string.
 * Missing → default constant (−15) for callers that opt in via hasExplicit…
 * `0` / `off` / `none` → disabled.
 */
export function resolveTourPitchCeilingDeg(search: string): number {
  const raw = new URLSearchParams(search).get('tourPitchCeiling')
  if (raw == null || raw === '') {
    return DEFAULT_TOUR_PITCH_CEILING_DEG
  }
  if (
    raw === '0' ||
    raw.toLowerCase() === 'off' ||
    raw.toLowerCase() === 'none'
  ) {
    return 0
  }
  const n = Number(raw)
  if (!Number.isFinite(n) || n >= 0 || n < -90) {
    return DEFAULT_TOUR_PITCH_CEILING_DEG
  }
  return n
}

/** True when ?tourPitchCeiling= was explicitly set (including off). */
export function hasExplicitTourPitchCeiling(search: string): boolean {
  const raw = new URLSearchParams(search).get('tourPitchCeiling')
  return raw != null && raw !== ''
}

/**
 * Max range from query. Missing → disabled (Infinity).
 * `?tourMaxRangeFt=35` enables; `off`/`none` disables.
 */
export function resolveTourMaxRangeFt(search: string): number {
  const raw = new URLSearchParams(search).get('tourMaxRangeFt')
  if (raw == null || raw === '') {
    return DEFAULT_TOUR_MAX_RANGE_FT
  }
  if (raw.toLowerCase() === 'off' || raw.toLowerCase() === 'none') {
    return Number.POSITIVE_INFINITY
  }
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) {
    return DEFAULT_TOUR_MAX_RANGE_FT
  }
  return n
}

/**
 * Plan-pixel distance → feet when ipp known; else via d_ref spacing assumption.
 */
export function planDistanceToFeet(
  distancePx: number,
  options: {
    inchesPerPixel?: number | null
    referenceDistancePx?: number
    uncalibratedMedianSpacingFt?: number
  } = {},
): number | null {
  if (!(distancePx > 0)) {
    return null
  }
  const inchesPerPixel = options.inchesPerPixel
  if (inchesPerPixel != null && inchesPerPixel > 0) {
    return (distancePx * inchesPerPixel) / 12
  }
  const ref = options.referenceDistancePx
  if (ref != null && ref > 0) {
    const spacingFt = options.uncalibratedMedianSpacingFt ?? 12
    return spacingFt * (distancePx / ref)
  }
  return null
}

/** True when the neighbor is within max range (or range cannot be estimated). */
export function isWithinTourMaxRange(
  distancePx: number,
  maxRangeFt: number,
  options: {
    inchesPerPixel?: number | null
    referenceDistancePx?: number
  } = {},
): boolean {
  if (!Number.isFinite(maxRangeFt) || maxRangeFt === Number.POSITIVE_INFINITY) {
    return true
  }
  const feet = planDistanceToFeet(distancePx, options)
  if (feet == null) {
    return true
  }
  return feet <= maxRangeFt
}

export function applyTourPitchCeiling(
  pitchDeg: number,
  pitchCeilingDeg: number = 0,
): number {
  if (!Number.isFinite(pitchCeilingDeg) || pitchCeilingDeg >= 0) {
    return pitchDeg
  }
  return Math.min(pitchDeg, pitchCeilingDeg)
}

export type GetPitchOptions = {
  /** Trusted inches per plan pixel only (from resolveTrustedTourInchesPerPixel). */
  inchesPerPixel?: number | null
  /** Floor plan scale for live-prod parity path (scale=1 → H = height). */
  legacyScale?: number | null
  /**
   * Optional presentation clamp (negative deg). Default 0 = off.
   * Pass only when `?tourPitchCeiling=` is explicit.
   */
  pitchCeilingDeg?: number
}

/**
 * Live-prod hypotenuse pitch (not pure −atan(H/d)).
 * Trusted API ipp → H = height/ipp; else H = height/scale (calibrated
 * legacy scale always used — live parity; scale=1 → H=height).
 */
export function getPitch(
  distance: number,
  scaleOrInchesPerPixel: number | null | undefined,
  imageHeightInches: number,
  options: GetPitchOptions = {},
): number {
  if (distance <= 0) {
    return -90
  }

  const height =
    Number.isFinite(imageHeightInches) && imageHeightInches > 0
      ? imageHeightInches
      : 48

  const trustedIpp =
    options.inchesPerPixel !== undefined
      ? options.inchesPerPixel
      : null

  let cameraHeightPx: number
  if (trustedIpp != null && trustedIpp > 0) {
    cameraHeightPx = height / trustedIpp
  } else {
    // Live prod: H = height/scale. Calibrated legacy scale (≠ 1) applies
    // even when API ipp was ignored (e.g. NexLoc). Uncalibrated → scale 1.
    const scale =
      options.legacyScale !== undefined
        ? options.legacyScale
        : scaleOrInchesPerPixel
    const effectiveScale =
      Number.isFinite(scale) && (scale as number) > 0 ? (scale as number) : 1
    // DB default 1 and invalid → treat as 1 (H = height).
    const safeScale = isFloorPlanScaleCalibrated(effectiveScale)
      ? effectiveScale
      : 1
    cameraHeightPx = height / safeScale
  }

  // Live didge TourManager formula (pre −atan rewrite). Dixon A4L2/938
  // d=144 height=90 → ≈ −27.92°, not −atan(90/144) ≈ −32°.
  const hypot = Math.sqrt(
    Math.pow(distance, 2) + Math.pow(cameraHeightPx, 2),
  )
  const geometric = (Math.atan2(hypot, cameraHeightPx) / Math.PI) * 180 - 90
  return applyTourPitchCeiling(geometric, options.pitchCeilingDeg ?? 0)
}
